import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { InView } from 'react-intersection-observer'
import { useInfiniteQuery } from 'react-query'
import BeatLoader from 'react-spinners/BeatLoader'

import UserCardEarnings from 'components/UI/UserCard/UserCardEarnings'
import { getPayoutsTransactions } from 'services/endpoints/earning'
import { useSalesCacheStore } from 'store/salesCacheStore'
import { type InfinitePages, type SalesTransaction } from 'types/api/earning'

const CACHE_EXPIRY_MS = 30 * 60 * 1000

type ExtendedSalesTransaction = SalesTransaction & {
  refund?: boolean
  chargeback?: number
  username?: string
  user_id?: string
  amount?: number
}

export const Sales = () => {
  const [salesFeed, setSalesFeed] = useState<ExtendedSalesTransaction[]>([])
  const [dataReady, setDataReady] = useState(false)

  const { t } = useTranslation()

  const {
    sales: cachedSales,
    lastFetched,
    setSales,
    setLastFetched,
  } = useSalesCacheStore()

  const { data, isFetching, fetchNextPage } = useInfiniteQuery<InfinitePages<SalesTransaction>>(
    ['getPayouts'],
    async ({ pageParam = 1 }) => await getPayoutsTransactions({ page: pageParam }),
    {
      getNextPageParam: lastPage => lastPage.nextCursor,
      refetchOnWindowFocus: false,
    }
  )

  // Load cached sales immediately
  useEffect(() => {
    const now = Date.now()
    if (cachedSales.length) {
      setSalesFeed(cachedSales as ExtendedSalesTransaction[])
      setDataReady(true)
    }

    const isCacheStale = !lastFetched || now - lastFetched > CACHE_EXPIRY_MS
    if (!cachedSales.length || isCacheStale) {
      setDataReady(false) // force fresh loader if no cache
    }
  }, [])

  // Update with fresh data when available
  useEffect(() => {
    if (data && !isFetching) {
      const tmp = data.pages.flatMap(page => page.page.data.data.results)
      setSalesFeed(tmp as ExtendedSalesTransaction[])
      setSales(tmp)
      setLastFetched(Date.now())
      setDataReady(true)
    }
  }, [data, isFetching, setSales])
useEffect(() => {
  let lastTop = window.scrollY
  let ticking = false
  let hasUserScrolled = false

  // Hide immediately on first downward movement
  const SHOW_AT_TOP_PX = 0
  const DELTA_PX = 1

  const setHeaderNavHidden = (hidden: boolean) => {
    document.body.classList.toggle('header-nav-hidden', hidden)
  }

  const enableScrollAnimation = () => {
    if (!document.body.classList.contains('header-scroll-animating')) {
      document.body.classList.add('header-scroll-animating')
    }
  }

  const resetHeaderNav = () => {
    document.body.classList.remove('header-nav-hidden')
    document.body.classList.remove('header-scroll-animating')
    document.body.classList.remove('header-nav-ready')
  }

  // Reset on mount
  resetHeaderNav()

  // Enable transitions after first paint only
  requestAnimationFrame(() => {
    document.body.classList.add('header-nav-ready')
  })

  const onScroll = () => {
    hasUserScrolled = true

    const top = window.scrollY
    const delta = top - lastTop

    if (!ticking) {
      ticking = true

      requestAnimationFrame(() => {
        ticking = false

        if (!hasUserScrolled) return

        enableScrollAnimation()

        // Fully visible only at exact top
        if (top <= SHOW_AT_TOP_PX) {
          setHeaderNavHidden(false)
          lastTop = top
          return
        }

        // Ignore micro jitter
        if (Math.abs(delta) < DELTA_PX) {
          lastTop = top
          return
        }

        // Hide immediately when scrolling down
        // Show immediately when scrolling up
        setHeaderNavHidden(delta > 0)

        lastTop = top
      })
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true })

  return () => {
    window.removeEventListener('scroll', onScroll)
    resetHeaderNav()
  }
}, [])

if (!dataReady) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '20rem',
      }}
    >
      <BeatLoader size={8} />
    </div>
  )
}

  return (
    <>
      {salesFeed.map((tx, index) => {
        const transaction = tx;
        const key = transaction.id || `tx-${index}`

        const card = (
          <UserCardEarnings
            user={{
              name: transaction.name,
              display_name: transaction.name,
              avatar: { url: transaction.avatar },
              username: transaction.username,
              id: transaction.user_id,
            }}
            earningsCardType={t(transaction.product)}
            earnings={transaction.amount}
            refunded={transaction.refund}
            chargeback={transaction.chargeback}
            userCardSubType="sale"
            hasDropDown
            iconSize="60"
            created={transaction.date}
            dropDownContent={
              <div className="sales-card-dropdown">
                <div className="sales-dropdown-card-content-container">
                  <div className="sales-dropdown-card-info">
                    <div className="sales-dropdown-card-info-type">{t('fee')}</div>
                    <div className="sales-dropdown-card-info-value">
                      ${transaction.fee}
                    </div>
                  </div>
                  <div className="sales-dropdown-card-info">
                    <div className="sales-dropdown-card-info-type">{t('net')}</div>
                    <div className="sales-dropdown-card-info-value">
                      ${transaction.gross}
                    </div>
                  </div>
                </div>
              </div>
            }
          />
        )

        return index === salesFeed.length - 1 ? (
          <InView
            key={key}
            as="div"
            threshold={0.1}
            triggerOnce
            onChange={(inView) => {
              if (inView && !isFetching) fetchNextPage()
            }}
          >
            {card}
          </InView>
        ) : (
          <div key={key}>{card}</div>
        )
      })}
    </>
  )
}
